/**
 * QaThreadsSection — assessor Q&A on a register item.
 *
 * PR-Cluster-6-b (docs/ux-design-plan.md §10). Renders as a section
 * beneath the register-item detail so a workspace member (Marcus /
 * Nadia) sees every question an assessor (Jordan) opened against
 * this control, and can reply, mark-addressed, or resolve.
 *
 * Threads are opened ONLY from assessor sessions (server-side gate
 * in qa.ts). This section is read + reply only for workspace members.
 *
 * Deferred:
 *   - Attach-evidence paperclip on the composer (P-C6-4) — needs a
 *     lightweight evidence-artifact picker component.
 *   - Reassign-thread affordance (BH24) — needs
 *     POST /qa/threads/:id/reassign backend endpoint.
 *   - Quick-reply templates (BH25) — canned patterns Jordan asks
 *     every engagement.
 *   - Composer identity per session (BH27) — currently the server
 *     stamps author_role from the session; SPA doesn't distinguish
 *     if you're posting-as-yourself yet.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Field,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../../lib/workspace';
import {
  getQaThread,
  listQaThreads,
  markQaThreadAddressed,
  postQaMessage,
  resolveQaThread,
  type QaAuthorRole,
  type QaMessageRow,
  type QaThreadRow,
  type QaThreadStatus,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';

export function QaThreadsSection({ registerItemId }: { registerItemId: string }) {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canResolve =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';
  const isAssessor = false; // client-side surface never runs under an
  // assessor session (the assessor's session drives the /assessor
  // routes); this flag exists to make future rewires explicit.

  const [threads, setThreads] = useState<QaThreadRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await listQaThreads(slug, {
          target_kind: 'register_item',
          target_id: registerItemId,
        });
        if (!cancelled) setThreads(res.threads);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load Q&A threads.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, registerItemId, reload]);

  const refresh = useCallback(() => setReload((r) => r + 1), []);

  if (!slug) return null;

  const openThreads = threads?.filter((t) => t.status !== 'resolved') ?? [];
  const resolvedThreads = threads?.filter((t) => t.status === 'resolved') ?? [];

  return (
    <section
      aria-labelledby="qa-section-heading"
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 id="qa-section-heading" style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
            Q&amp;A from assessor
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
            Questions your 3PAO opened against this control. Reply here; the assessor's session
            sees your response in the audit-scoped view.
          </p>
        </div>
        {threads && threads.length > 0 ? (
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {openThreads.length} open · {resolvedThreads.length} resolved
          </span>
        ) : null}
      </header>

      {loading ? (
        <Spinner label="Loading Q&A" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : threads == null || threads.length === 0 ? (
        <EmptyQa />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {openThreads.map((t) => (
            <ThreadCard
              key={t.thread_id}
              slug={slug}
              thread={t}
              canResolve={canResolve}
              isAssessor={isAssessor}
              defaultOpen
              onChanged={refresh}
            />
          ))}
          {resolvedThreads.length > 0 ? (
            <details style={{ marginTop: 'var(--space-2)' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-3xs)',
                  padding: 'var(--space-1) 0',
                }}
              >
                Show {resolvedThreads.length} resolved
              </summary>
              <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                {resolvedThreads.map((t) => (
                  <ThreadCard
                    key={t.thread_id}
                    slug={slug}
                    thread={t}
                    canResolve={canResolve}
                    isAssessor={isAssessor}
                    defaultOpen={false}
                    onChanged={refresh}
                  />
                ))}
              </div>
            </details>
          ) : null}
        </div>
      )}
    </section>
  );
}

function EmptyQa() {
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-2xs)',
        background: 'var(--surface-alt)',
        borderRadius: 'var(--radius-card)',
        textAlign: 'center',
      }}
    >
      No assessor questions on this item yet.
    </div>
  );
}

// ---- Thread card -----------------------------------------------------

function ThreadCard(props: {
  slug: string;
  thread: QaThreadRow;
  canResolve: boolean;
  isAssessor: boolean;
  defaultOpen: boolean;
  onChanged: () => void;
}) {
  const { thread: t, canResolve, onChanged, slug } = props;
  const [expanded, setExpanded] = useState(props.defaultOpen);
  const [messages, setMessages] = useState<QaMessageRow[] | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [addressing, setAddressing] = useState(false);

  useEffect(() => {
    if (!expanded || messages != null) return;
    let cancelled = false;
    setMsgLoading(true);
    setMsgError(null);
    (async () => {
      try {
        const res = await getQaThread(slug, t.thread_id);
        if (!cancelled) setMessages(res.messages);
      } catch (err) {
        if (!cancelled) setMsgError(err instanceof Error ? err.message : 'Failed to load messages.');
      } finally {
        if (!cancelled) setMsgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, messages, slug, t.thread_id]);

  const refetch = useCallback(async () => {
    try {
      const res = await getQaThread(slug, t.thread_id);
      setMessages(res.messages);
    } catch (err) {
      setMsgError(err instanceof Error ? err.message : 'Failed to reload messages.');
    }
  }, [slug, t.thread_id]);

  const markAddressed = async () => {
    setAddressing(true);
    try {
      await markQaThreadAddressed(slug, t.thread_id);
      onChanged();
    } catch (err) {
      setMsgError(err instanceof Error ? err.message : 'Mark-addressed failed.');
    } finally {
      setAddressing(false);
    }
  };

  return (
    <article
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: statusBorder(t.status),
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-elevated)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          appearance: 'none',
          background: 'transparent',
          border: 0,
          padding: 'var(--space-3) var(--space-4)',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          color: 'var(--text)',
          fontSize: 'var(--font-size-2xs)',
        }}
      >
        <span aria-hidden style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
          {expanded ? '▾' : '▸'}
        </span>
        <StatusPill status={t.status} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)' }} title={t.opened_by}>
            {t.opened_by}
          </span>
          <span aria-hidden style={{ color: 'var(--text-dim)', margin: '0 6px' }}>·</span>
          <time dateTime={t.opened_at} style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
            {fmtDate(t.opened_at)}
          </time>
        </span>
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)', fontFamily: 'var(--font-mono)' }} title={t.thread_id}>
          {t.thread_id.slice(0, 12)}…
        </span>
      </button>

      {expanded ? (
        <div style={{ padding: '0 var(--space-4) var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
          {msgLoading ? (
            <Spinner label="Loading messages" />
          ) : msgError ? (
            <Alert variant="error">{msgError}</Alert>
          ) : messages == null ? null : messages.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
              No messages yet.
            </div>
          ) : (
            <ol style={{ display: 'grid', gap: 'var(--space-2)', listStyle: 'none', padding: 0, margin: 0 }}>
              {messages.map((m) => (
                <MessageBubble key={m.message_id} message={m} />
              ))}
            </ol>
          )}

          {t.status !== 'resolved' ? (
            <ReplyComposer slug={slug} threadId={t.thread_id} onSent={refetch} />
          ) : (
            <div
              style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--text-muted)',
              }}
            >
              Resolved {fmtDate(t.resolved_at ?? t.opened_at)}
              {t.resolved_reason ? (
                <>
                  {' — '}
                  <span style={{ fontStyle: 'italic' }}>“{t.resolved_reason}”</span>
                </>
              ) : null}
            </div>
          )}

          {t.status !== 'resolved' ? (
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', paddingTop: 'var(--space-1)', borderTop: '1px solid var(--border)' }}>
              {t.status === 'open' ? (
                <Button variant="ghost" compact onClick={() => void markAddressed()} disabled={addressing}>
                  {addressing ? 'Marking…' : 'Mark addressed'}
                </Button>
              ) : null}
              {canResolve ? (
                <Button variant="ghost" compact onClick={() => setShowResolve((s) => !s)}>
                  {showResolve ? 'Cancel resolve' : 'Resolve thread'}
                </Button>
              ) : null}
            </div>
          ) : null}

          {showResolve ? (
            <ResolveForm
              slug={slug}
              threadId={t.thread_id}
              onDone={() => {
                setShowResolve(false);
                onChanged();
              }}
              onCancel={() => setShowResolve(false)}
              onError={setMsgError}
            />
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function MessageBubble({ message }: { message: QaMessageRow }) {
  const isAssessor = message.author_role === 'assessor';
  return (
    <li
      style={{
        display: 'grid',
        gap: '2px',
        padding: 'var(--space-2) var(--space-3)',
        background: isAssessor ? 'var(--warning-bg)' : 'var(--surface)',
        border: `1px solid ${isAssessor ? 'var(--band-amber)' : 'var(--border)'}`,
        borderLeftWidth: '3px',
        borderRadius: '4px',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <AuthorRoleBadge role={message.author_role} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }} title={message.author_user_id}>
          {message.author_user_id}
        </span>
        <time dateTime={message.posted_at} style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          {fmtDate(message.posted_at)}
        </time>
      </div>
      <p
        style={{
          margin: '4px 0 0',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {message.body}
      </p>
      {message.evidence_artifact_id ? (
        <div style={{ marginTop: 4, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          📎 evidence: <code style={{ fontFamily: 'var(--font-mono)' }} title={message.evidence_artifact_id}>{message.evidence_artifact_id}</code>
        </div>
      ) : null}
    </li>
  );
}

function AuthorRoleBadge({ role }: { role: QaAuthorRole }) {
  const tone: Record<QaAuthorRole, { bg: string; fg: string; label: string }> = {
    assessor: { bg: 'var(--band-amber)', fg: 'var(--text-inverse)', label: 'Assessor' },
    workspace_owner: { bg: 'var(--surface-alt)', fg: 'var(--text)', label: 'Owner' },
    workspace_member: { bg: 'var(--surface-alt)', fg: 'var(--text)', label: 'Member' },
  };
  const t = tone[role];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: '3px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontWeight: 700,
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function StatusPill({ status }: { status: QaThreadStatus }) {
  const tone: Record<QaThreadStatus, { bg: string; fg: string; label: string }> = {
    open: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', label: 'Open' },
    addressed: { bg: 'var(--accent)', fg: 'var(--text-inverse)', label: 'Addressed' },
    resolved: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Resolved' },
  };
  const t = tone[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function statusBorder(status: QaThreadStatus): string {
  switch (status) {
    case 'open':
      return 'var(--band-amber)';
    case 'addressed':
      return 'var(--accent)';
    case 'resolved':
      return 'var(--band-green)';
  }
}

// ---- Reply composer + Resolve form ----------------------------------

function ReplyComposer(props: { slug: string; threadId: string; onSent: () => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await postQaMessage(props.slug, props.threadId, { body: body.trim() });
      setBody('');
      props.onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reply failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <Field id={`qa-reply-${props.threadId}`} label="Reply">
        <Textarea
          id={`qa-reply-${props.threadId}`}
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type your reply. Attach evidence via the register-item Evidence tab; a paperclip composer lands in a follow-on PR."
        />
      </Field>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" compact type="submit" disabled={busy || body.trim().length === 0}>
          {busy ? 'Sending…' : 'Post reply'}
        </Button>
      </div>
    </form>
  );
}

function ResolveForm(props: {
  slug: string;
  threadId: string;
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await resolveQaThread(props.slug, props.threadId, reason.trim());
      props.onDone();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Resolve failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--success-bg)',
        border: '1px solid var(--band-green)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--success-fg)', fontWeight: 700 }}>Resolve thread</strong>
      <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--success-fg)' }}>
        Reason ≥ 10 chars, audit-logged. Once resolved, only the assessor can re-open within the
        engagement window.
      </p>
      <Field id={`resolve-${props.threadId}`} label="Reason">
        <Textarea
          id={`resolve-${props.threadId}`}
          required
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || reason.trim().length < 10}>
          {busy ? 'Resolving…' : 'Confirm resolve'}
        </Button>
      </div>
    </form>
  );
}

// Badge import kept for tree-shaking symmetry with sibling detail
// sections that reach for it (evidence chips, framework chips).
void Badge;
